/**
 * Route Prefetching Utilities
 * 
 * Implements intelligent route prefetching to improve perceived navigation speed:
 * - Hover prefetching: Prefetch routes when user hovers over links
 * - Idle prefetching: Prefetch likely next routes during browser idle time
 * - Priority-based prefetching: Prefetch critical routes first
 * 
 * @module routePrefetch
 */

/**
 * Cache of prefetched routes to avoid duplicate prefetching
 */
const prefetchedRoutes = new Set();

/**
 * Prefetch a route by dynamically importing its lazy-loaded component
 * 
 * @param {string} routePath - The route path to prefetch
 * @param {Function} importFn - The dynamic import function for the route
 * @returns {Promise<void>}
 */
export const prefetchRoute = async (routePath, importFn) => {
  if (prefetchedRoutes.has(routePath)) {
    return;
  }

  try {
    prefetchedRoutes.add(routePath);
    
    await importFn();
  } catch (error) {
    prefetchedRoutes.delete(routePath);
    console.warn(`Failed to prefetch route: ${routePath}`, error);
  }
};

/**
 * Setup hover prefetching for a link element
 * 
 * @param {HTMLElement} linkElement - The link element to setup prefetching for
 * @param {string} routePath - The route path to prefetch
 * @param {Function} importFn - The dynamic import function for the route
 */
export const setupHoverPrefetch = (linkElement, routePath, importFn) => {
  if (!linkElement || !routePath || !importFn) {
    return;
  }

  const handleMouseEnter = () => {
    prefetchRoute(routePath, importFn);
  };

  linkElement.addEventListener('mouseenter', handleMouseEnter, { once: true });
  
  return () => {
    linkElement.removeEventListener('mouseenter', handleMouseEnter);
  };
};

/**
 * Prefetch routes during browser idle time
 * 
 * @param {Array<{path: string, importFn: Function, priority: number}>} routes - Routes to prefetch
 */
export const prefetchOnIdle = (routes) => {
  if (!routes || routes.length === 0) {
    return;
  }

  const sortedRoutes = [...routes].sort((a, b) => (b.priority || 0) - (a.priority || 0));

  const prefetchNext = (index = 0) => {
    if (index >= sortedRoutes.length) {
      return;
    }

    const route = sortedRoutes[index];
    
    const scheduleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    
    scheduleCallback(() => {
      prefetchRoute(route.path, route.importFn).then(() => {
        setTimeout(() => prefetchNext(index + 1), 100);
      });
    });
  };

  prefetchNext();
};

/**
 * Get likely next routes based on current location
 * 
 * @param {string} currentPath - Current route path
 * @returns {Array<string>} Array of likely next route paths
 */
export const getLikelyNextRoutes = (currentPath) => {
  const navigationPatterns = {
    '/': ['/admin/dashboard', '/manager/dashboard', '/operator/dashboard'],
    '/admin/dashboard': ['/admin/orders', '/admin/products', '/admin/inventory'],
    '/admin/orders': ['/admin/products', '/admin/customers', '/admin/shipping'],
    '/admin/products': ['/admin/orders', '/admin/inventory', '/admin/categories'],
    '/admin/inventory': ['/admin/products', '/admin/warehouses', '/admin/cycle-count'],
    '/manager/dashboard': ['/manager/wms', '/manager/returns-forecast', '/manager/analytics'],
    '/operator/dashboard': ['/operator/inventory', '/operator/picking', '/operator/cycle-count']
  };

  return navigationPatterns[currentPath] || [];
};

/**
 * Setup intelligent prefetching based on current location
 * 
 * @param {string} currentPath - Current route path
 * @param {Object} routeImports - Map of route paths to import functions
 */
export const setupIntelligentPrefetch = (currentPath, routeImports) => {
  const likelyRoutes = getLikelyNextRoutes(currentPath);
  
  const routesToPrefetch = likelyRoutes
    .filter(path => routeImports[path])
    .map((path, index) => ({
      path,
      importFn: routeImports[path],
      priority: likelyRoutes.length - index // Higher priority for earlier routes
    }));

  if (routesToPrefetch.length > 0) {
    prefetchOnIdle(routesToPrefetch);
  }
};

/**
 * Clear prefetch cache (useful for testing or memory management)
 */
export const clearPrefetchCache = () => {
  prefetchedRoutes.clear();
};
